import { DocStatusEnum, type TDocument } from '~/modules/identification/forms/Documents/types.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import {
  GuaranteesFetchStatus,
  GuaranteesFileType,
  type TGuaranteesApplication,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import { UDBTF_APPROVAL_LETTER_TEXT } from './constants.ts';

export const resolveInternalApprovalRedirectPath = ({
  baseRedirectPath,
  hasCurrentRouteApplication,
  hasFreshCurrentApplication,
  isInternalApprovalInProgress,
  modalSuccessText,
  tasksPath,
}: {
  baseRedirectPath: string;
  hasCurrentRouteApplication: boolean;
  hasFreshCurrentApplication: boolean;
  isInternalApprovalInProgress: boolean;
  modalSuccessText: string;
  tasksPath: string;
}): string => {
  if (modalSuccessText) {
    return '';
  }

  if (baseRedirectPath) {
    return baseRedirectPath;
  }

  return hasFreshCurrentApplication && hasCurrentRouteApplication && !isInternalApprovalInProgress
    ? tasksPath
    : '';
};

const isApplicationForRoute = (
  application: TGuaranteesApplication | null | undefined,
  routeApplicationId: string
): boolean => {
  const normalizedRouteApplicationId = getStringValue(routeApplicationId);

  if (!application || !normalizedRouteApplicationId) {
    return false;
  }

  return [getStringValue(application.id), getStringValue(application.applicationId)]
    .filter(Boolean)
    .includes(normalizedRouteApplicationId);
};

export const resolveInternalApprovalRenderApplication = ({
  currentRouteApplication,
  previousRenderApplication,
  routeApplicationId,
}: {
  currentRouteApplication?: TGuaranteesApplication | null;
  previousRenderApplication?: TGuaranteesApplication | null;
  routeApplicationId: string;
}): TGuaranteesApplication | null => {
  if (currentRouteApplication) {
    return currentRouteApplication;
  }

  return isApplicationForRoute(previousRenderApplication, routeApplicationId)
    ? (previousRenderApplication ?? null)
    : null;
};

export const getInternalApprovalPageLoading = ({
  applicationStatus,
  baseRedirectPath,
  renderApplication,
}: {
  applicationStatus: GuaranteesFetchStatus;
  baseRedirectPath: string;
  renderApplication?: TGuaranteesApplication | null;
}): boolean =>
  !baseRedirectPath && !renderApplication && applicationStatus !== GuaranteesFetchStatus.Error;

export const getInternalApprovalLetterFile = (
  applicationFiles: TGuaranteesApplicationFile[] = [],
  fileType: GuaranteesFileType
): TGuaranteesApplicationFile | null =>
  applicationFiles.find((file) => file.fileType === fileType) ?? null;

export const getApprovalLetterPreviewFileId = (
  applicationFile?: TGuaranteesApplicationFile | null
): string => getStringValue(applicationFile?.fileId);

export const getApprovalLetterSigningFileId = (
  applicationFile?: TGuaranteesApplicationFile | null
): string => getStringValue(applicationFile?.fileId);

export const getApprovalLetterFileName = (
  applicationFile?: TGuaranteesApplicationFile | null,
  fallbackName: string = UDBTF_APPROVAL_LETTER_TEXT.previewTitle
): string => getStringValue(applicationFile?.description) || fallbackName;

export const buildApprovalLetterSigningDocument = (
  applicationFile?: TGuaranteesApplicationFile | null,
  fallbackName: string = UDBTF_APPROVAL_LETTER_TEXT.previewTitle,
  index: number = 0
): TDocument | null => {
  const signingFileId = getApprovalLetterSigningFileId(applicationFile);

  if (!signingFileId) {
    return null;
  }

  const fileName = getApprovalLetterFileName(applicationFile, fallbackName);

  return {
    id: signingFileId,
    nameDoc: fileName,
    idDictWebDocument: index + 1,
    idDoc: index + 1,
    docStatus: [DocStatusEnum.NOT_VERIFIED],
    documentName: fileName,
  };
};

export const buildApprovalLetterSigningDocuments = (
  documents: Array<{
    file?: TGuaranteesApplicationFile | null;
    fallbackName?: string;
  }>
): TDocument[] => {
  return documents
    .map(({ file, fallbackName }, index) =>
      buildApprovalLetterSigningDocument(file, fallbackName, index)
    )
    .filter((document): document is TDocument => !!document);
};

export const getApprovalLetterSigningDocumentPreviewFileId = (
  documents: TDocument[],
  documentId: number | string | undefined
): string | undefined =>
  getStringValue(documents.find((document) => String(document.idDoc) === String(documentId))?.id) ||
  undefined;

export type TInternalApprovalSigningPreviewTarget = 'approval' | 'conclusion' | null;

export const getInternalApprovalSigningPreviewTarget = ({
  documents,
  documentId,
  approvalLetterFileId,
  conclusionAttachmentFileId,
}: {
  documents: TDocument[];
  documentId: number | string | undefined;
  approvalLetterFileId: string;
  conclusionAttachmentFileId: string;
}): TInternalApprovalSigningPreviewTarget => {
  const previewFileId = getApprovalLetterSigningDocumentPreviewFileId(documents, documentId);
  if (previewFileId === approvalLetterFileId) {
    return 'approval';
  }
  if (previewFileId === conclusionAttachmentFileId) {
    return 'conclusion';
  }
  return null;
};
